import styled, { css } from 'styled-components';
import { Input } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { Label, Text } from '../Typography';

const BREAKPOINT_NH = {
  layoutShift1: theme.breakpoint.large,
  layoutShift2: theme.breakpoint.xlarge,
};

// Item
export const Layout = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  min-width: fit-content;
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${toRem(4)};
  overflow-y: visible;
  margin: 0 0 ${toRem(theme.space.lg)} 0;
  background-color: ${theme.color.neutral[50]};
`;

// Item Header
export const Header = styled.header<{ $wrap?: boolean }>`
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: ${toRem(66)};
  padding: ${toRem(theme.space.xl)} ${toRem(theme.space.xl)} ${toRem(theme.space.lg)} ${toRem(theme.space.xl)};

  ${({ $wrap }) =>
    $wrap &&
    css`
      @media (max-width: ${theme.breakpoint.xlarge}px) {
        flex-wrap: wrap;
        gap: ${toRem(theme.space.xl)};
      }
    `}
`;

export const HeaderSection = styled.section`
  align-items: center;
  height: 100%;

  &.user-info {
    flex-grow: 1;
    min-width: ${toRem(260)};
    word-break: break-word;
  }

  &:last-child {
    justify-content: flex-end;
    flex-basis: ${toRem(100)};
    flex-shrink: 4;

    > * {
      margin-left: ${toRem(theme.space.sm)};

      &:first-child {
        margin-left: 0;
      }
    }
  }
`;

export const HeaderError = styled.div`
  justify-content: center;
  text-align: center;
  width: 100%;
  padding: ${toRem(theme.space.sm)};
  color: ${theme.color.neutral[900]};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(theme.font.size.md)};
  border-radius: ${theme.borderRadius.xsmall}px ${theme.borderRadius.xsmall}px 0 0;
  border-bottom: 1px solid ${theme.color.neutral[200]};
  background: ${theme.color.warning[25]};
`;

// User Info
export const UserInfoLayout = styled.div`
  flex-grow: 1;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  height: auto;
`;

export const UserInfo = styled.section`
  flex-grow: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 0 0 0 ${toRem(theme.space.md)};

  > *:not(:last-child) {
    margin-bottom: ${toRem(theme.space.xs)};

    @media (min-width: ${theme.breakpoint.small}px) {
      margin-bottom: ${toRem(theme.space.sm)};
    }
  }
`;

export const StyledText = styled(Text).attrs({
  size: 'small',
})`
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[900]};
`;

export const IconButton = styled.button.attrs({
  type: 'button',
})`
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  width: ${toRem(24)};
  height: ${toRem(24)};
  background-color: ${theme.color.white};
  border-radius: 50%;
  overflow: hidden;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};

  svg {
    fill: ${theme.color.neutral[800]};
  }

  &:hover,
  &:focus {
    background-color: ${theme.color.neutral[100]};
    svg {
      fill: ${theme.color.neutral[900]};
    }
  }

  &:disabled {
    background-color: ${theme.color.neutral[200]};
    cursor: auto;

    svg {
      fill: ${theme.color.neutral[400]};
    }
  }
`;

// Bottom
export const Bottom = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
  height: auto;
  padding: 0 ${toRem(theme.space.xl)};
`;

export const InputForm = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;

  > * {
    margin-bottom: ${toRem(theme.space.lg)};
  }

  @media (min-width: ${BREAKPOINT_NH.layoutShift1}px) {
    display: flex;
  }

  @media (min-width: ${BREAKPOINT_NH.layoutShift2}px) {
    display: none;
  }
`;

export const DesktopInputContainer = styled.div`
  > * {
    max-width: ${toRem(150)};
    margin-right: ${toRem(theme.space.xl)};

    &:last-child {
      width: auto;
    }
  }

  input {
    max-width: ${toRem(125)};
  }
`;

export const CurrencyInputContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;

  > div,
  > p {
    margin-top: ${toRem(theme.space.xs)};
  }

  p {
    display: flex;
    flex-flow: row nowrap;
    width: 100%;
    text-align: left;
  }
`;

export const NoteWrapper = styled.div`
  border-top: 1px solid ${theme.color.neutral[200]};
  padding: ${toRem(theme.space.md)} 0 ${toRem(theme.space.md)};
`;

export const NoteInput = styled(Input)`
  flex-shrink: 0;
`;

export const DesktopCurrencyInputContainer = styled(CurrencyInputContainer)`
  display: none;

  @media (min-width: ${BREAKPOINT_NH.layoutShift1}px) {
    display: none;
  }

  @media (min-width: ${BREAKPOINT_NH.layoutShift2}px) {
    display: flex;
    width: ${toRem(100)};
  }
`;

export const TopLabel = styled(Label)`
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  color: ${theme.color.neutral[500]};
`;

export const NetLabelWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${toRem(theme.input.height.large)};
  width: ${toRem(80)};
`;

export const NetLabel = styled(Label)`
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(theme.font.size.lg)};
  line-height: ${lineHeight(theme.font.size.lg)};
  width: ${toRem(80)};
`;
